import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import defaultNavItems from "./defaultNavItems";
import { rankResults } from "./rankResults";
import { Overlay, Modal, InputRow, KbdHint, ResultsList, ResultRow } from "./styles.spotlightSearch";
import TileIcon from "../../shared/Icon/TileIcon";

export default function SpotlightSearch({ items = defaultNavItems, open, onClose, onSelect }) {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef(null);

  function handleSelect(item) {
    if (onSelect) {
      onSelect(item);
    } else {
      navigate(item.path);
    }
    onClose();
  }

  const results = query ? rankResults(items, query) : items;

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIndex(0);
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, results.length - 1));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === "Enter" && results.length > 0) {
        const item = results[selectedIndex] ?? results[0];
        handleSelect(item);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, results, selectedIndex, navigate, onClose]);

  if (!open) return null;

  return createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={(event) => event.stopPropagation()}>
        <InputRow>
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setSelectedIndex(0);
            }}
            placeholder="Search..."
          />
          <KbdHint onClick={onClose}>Esc</KbdHint>
        </InputRow>
        <ResultsList>
          {results.map((item, index) => (
            <ResultRow
              key={item.key}
              $active={index === selectedIndex}
              onMouseEnter={() => setSelectedIndex(index)}
              onClick={() => handleSelect(item)}
            >
              <TileIcon src={item.icon} mobileSize="18px" />
              <span>{item.label}</span>
            </ResultRow>
          ))}
        </ResultsList>
      </Modal>
    </Overlay>,
    document.body
  );
}
